import type { ChangeEvent, PropsWithChildren } from 'react'
import React, { useRef, useEffect, forwardRef } from 'react'
import InputLabel from '../../../atoms/InputLabel'
import { Col, Row, Dropdown } from 'react-bootstrap'
import Icon from '../../../atoms/Icon'
import icons from '../../../../_constants/icons'
import { Hit } from '../../../../domain/models/rest/SearchResponse'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SearchResult {}

// Custom toggle component for the dropdown
interface CustomToggleProps {
  children: React.ReactNode
  [key: string]: unknown
}

const CustomToggle = forwardRef<HTMLDivElement, CustomToggleProps>(({ children, ...props }, ref) => (
  <div ref={ref} {...props}>
    {children}
  </div>
))

CustomToggle.displayName = 'CustomToggle'

type Props<T> = {
  label: string
  errorMessage?: string
  error?: boolean
  iconLeft?: string
  iconRight?: string
  callback: (event: ChangeEvent) => void
  dropdownItems: Hit<T>[]
  onItemSelect: (item: T) => void
  value: number | string | undefined
  itemLabel: (_source: T) => string
  onCloseDropdown?: () => void
}

const DropdownInput = <T extends SearchResult>({
  label,
  errorMessage,
  error,
  iconLeft,
  iconRight,
  callback,
  dropdownItems,
  onItemSelect,
  value,
  itemLabel,
  onCloseDropdown,
}: PropsWithChildren<Props<T>>): JSX.Element => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const showDropdown = dropdownItems.length > 0

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        onCloseDropdown
      ) {
        onCloseDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown, onCloseDropdown])

  return (
    <Col ref={dropdownRef} className="p-0 mt-0 mb-0 dropdown-input">
      {label && (
        <Row className="m-0">
          <InputLabel label={label} />
        </Row>
      )}
      <Row className={`m-0 input ${error ? 'validation-error' : ''}`}>
        <Col className="p-0 justify-content-center">
          <Dropdown
            show={showDropdown}
            onToggle={() => onCloseDropdown?.()}
            onSelect={(selectedValue) => {
              const selectedItem = dropdownItems.find((item) => String(item._id) === selectedValue)
              if (selectedItem) {
                onItemSelect(selectedItem._source)
              }
            }}
          >
            <Dropdown.Toggle as={CustomToggle} bsPrefix="custom-dropdown-toggle">
              <div className="dropdown-input-container position-relative">
                {iconLeft && <Icon name={iconLeft} className="dropdown-input-icon-left" />}
                <input
                  ref={inputRef}
                  onChange={(e: ChangeEvent) => callback(e)}
                  className="dropdown-input-control"
                  type={!!value ? typeof value : 'text'}
                  name={label}
                  data-testid="dropdownInput"
                  value={value || ''}
                  autoComplete="off"
                />
                {iconRight && <Icon name={iconRight} className="dropdown-input-icon-right" />}
                {error && <Icon name={icons.ERROR} className="dropdown-input-error-icon" />}
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="dropdown-input-menu">
              {dropdownItems.map((item: Hit<T>) => (
                <Dropdown.Item key={item._id} eventKey={String(item._id)} data-testid="dropdownValue">
                  {itemLabel(item._source)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
      {error && errorMessage && (
        <Row className="m-0 pl-3 pt-2">
          <p className="error">{errorMessage}</p>
        </Row>
      )}
    </Col>
  )
}

export default DropdownInput
